import { FC, useState } from 'react';

import { GoogleLogin } from '@react-oauth/google';
import images from 'assets/images';
import AddressForm from 'components/form/AddressForm';
import PassworkValidatingStatus from 'components/form/PassworkValidatingStatus';
import AuthWrapper from 'components/modules/AuthWrapper';
import Button from 'components/primitives/Button';
import PasswordInput from 'components/primitives/PasswordInput';
import SelectInput from 'components/primitives/SelectInput';
import SelectOption from 'components/primitives/SelectOption';
import TextInput from 'components/primitives/TextInput';
import StepIndicator from 'components/ui/StepIndicator';
import { Form, Formik } from 'formik';
import { isDev } from 'utils/environment';
import { SignupViewProps } from './Signup.props';
import {
  basicInfoValidationSchema,
  getAddressValidationSchema,
  initialValues,
} from './Signup.utils';

type CountryCode = {
  title: string;
  code: string;
};

const productionCountryCodes: CountryCode[] = [{ title: 'US', code: '+1' }];

const stagingCountryCodes = [
  { title: 'IN', code: '+91' },
  { title: 'ES', code: '+34' },
  { title: 'UY', code: '+598' },
  { title: 'AR', code: '+54' },
];

const SignupView: FC<SignupViewProps> = ({
  onSubmit,
  goToLogin,
  isSubmitting = false,
  isGoogleLoading = false,
  onGoogleSignup,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showPasswordStatus, setShowPasswordStatus] = useState<boolean>(false);
  const [formData, setFormData] = useState(initialValues);
  const [isGoogleSignupCompleted, setIsGoogleSignupCompleted] = useState(false);

  const devCountryCodes = isDev ? stagingCountryCodes : [];
  const countryCodes = [...productionCountryCodes, ...devCountryCodes];

  const getTitleFromCode = (code: string) => {
    return countryCodes.find((country) => country.code === code)?.title;
  };

  const handleStepOneSubmit = (values: typeof initialValues) => {
    setFormData({ ...formData, ...values });
    setCurrentStep(2);
  };

  const handleStepTwoSubmit = (values: typeof initialValues) => {
    const completeData = { ...formData, ...values };
    onSubmit(completeData);
  };

  const handleBackToStepOne = () => {
    setCurrentStep(1);
  };

  const handleGoogleSuccess = (credentialResponse: any) => {
    if (credentialResponse.credential) {
      setCurrentStep(2);
      setFormData({
        ...formData,
        idToken: credentialResponse.credential,
      });
      setIsGoogleSignupCompleted(true);
    }
  };

  const handleGoogleError = () => {
    console.error(
      'Google authentication failed. Please check your Google Client ID configuration.',
    );
    // The error will be handled by the mutation's onError handler
  };

  const handleCustomGoogleClick = () => {
    const hiddenGoogleButton = document.querySelector(
      '#hidden-google-signup div[role="button"]',
    ) as HTMLElement;
    if (hiddenGoogleButton) {
      hiddenGoogleButton.click();
    }
  };

  return (
    <AuthWrapper>
      <h2 className="text-center">Create your account</h2>
      <div className="justify-center items-center place-items-center m-4">
        <Formik
          initialValues={currentStep === 1 ? formData : formData}
          onSubmit={(values) => {
            if (currentStep === 1) {
              handleStepOneSubmit(values);
            } else if (isGoogleSignupCompleted) {
              onGoogleSignup?.({
                idToken: values.idToken,
                location: values.location,
                phone: values.phone,
                referralCode: values.referralCode,
                countrySymbol: values.countrySymbol,
                countryCode: values.countryCode,
              });
            } else {
              handleStepTwoSubmit(values);
            }
          }}
          validationSchema={
            currentStep === 1
              ? basicInfoValidationSchema
              : getAddressValidationSchema(isGoogleSignupCompleted)
          }
          enableReinitialize={true}>
          {({ values, setFieldValue }) => {
            return (
              <Form className="w-full">
                <StepIndicator
                  currentStep={currentStep}
                  totalSteps={2}
                  stepLabels={['Basic Info', 'Address']}
                />
                {currentStep === 1 && !isGoogleSignupCompleted && (
                  <>
                    {/* Hidden Google Signup Component */}
                    <div
                      id="hidden-google-signup"
                      className="hidden absolute left-[-9999px]">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        auto_select={false}
                      />
                    </div>

                    <div className="flex gap-4 justify-stretch">
                      <Button
                        variant="outline"
                        isDisabled={isGoogleSignupCompleted}
                        colorScheme="gray"
                        type="button"
                        onClick={handleCustomGoogleClick}
                        className="btn flex-1 border-1 border-[#999999] !text-[#333]">
                        <img
                          src={images.googleLogo}
                          width={22}
                          alt="Google Logo"
                        />{' '}
                        Sign up with Google
                      </Button>
                    </div>
                    <div className="divider my-6 text-sm">OR</div>
                  </>
                )}

                {currentStep === 1 ? (
                  <div className="flex flex-col justify-center">
                    {!isGoogleSignupCompleted && (
                      <>
                        <div className="flex gap-5">
                          <TextInput name="fname" placeholder="First Name" />
                          <TextInput name="lname" placeholder="Last Name" />
                        </div>

                        <TextInput
                          type="email"
                          name="email"
                          placeholder="Email"
                        />

                        <PasswordInput
                          name="password"
                          placeholder="Password"
                          hideErrorComponent={true}
                          onKeyUp={() => setShowPasswordStatus(true)}
                        />

                        <PassworkValidatingStatus hide={showPasswordStatus} />

                        <PasswordInput
                          name="confirmPassword"
                          placeholder="Repeat Password"
                        />

                        <div className="flex gap-5">
                          {isDev && (
                            <div className="w-[120px]">
                              <SelectInput
                                name="countryCode"
                                onChange={(value) => {
                                  const title = getTitleFromCode(value);
                                  setFieldValue('countrySymbol', title);
                                }}>
                                {countryCodes.map((item) => (
                                  <SelectOption
                                    key={item.code}
                                    value={item.code}
                                    child={item.title}
                                  />
                                ))}
                              </SelectInput>
                            </div>
                          )}

                          <TextInput
                            type="tel"
                            name="phone"
                            placeholder="Phone"
                          />
                        </div>

                        <TextInput
                          name="referralCode"
                          placeholder="Referral Code (optional)"
                        />

                        <p className="text-sm text-slate-500">
                          By entering my mobile number, I agree to receive
                          important account related texts from Rapid Rentals
                          such as rental confirmations and reminders. Message
                          frequency varies by account usage. Message & data
                          rates may apply. Reply STOP to opt out.
                        </p>

                        <Button
                          id="next-btn"
                          type="submit"
                          variant="solid"
                          colorScheme="primary"
                          isLoading={isSubmitting}
                          className="btn-var-solid w-full h-auto btn-var-solid mt-5">
                          Next
                        </Button>
                      </>
                    )}

                    {isGoogleSignupCompleted && (
                      <div className="text-center py-8">
                        <div className="text-[#22CC7A] mb-4">
                          <svg
                            className="w-16 h-16 mx-auto"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Google Account Connected!
                        </h3>
                        <p className="text-gray-600 mb-6 text-sm">
                          Your Google account has been successfully connected.
                          Please continue with adding your address information.
                        </p>
                        <Button
                          onClick={() => setCurrentStep(2)}
                          variant="solid"
                          colorScheme="primary"
                          className="w-full">
                          Continue to Address Information
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <AddressForm
                    onBack={handleBackToStepOne}
                    isGoogleSignupCompleted={isGoogleSignupCompleted}
                    isLoading={isSubmitting || isGoogleLoading}
                    setFieldValue={setFieldValue}
                    countryCodes={countryCodes}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </div>

      <div className="flex flex-row justify-center mt-5 text-gray-600">
        <p className="text-sm">Have an account?&nbsp;</p>
        <p
          onClick={goToLogin}
          className="text-sm text-primary-500 cursor-pointer">
          Log into your account
        </p>
      </div>
    </AuthWrapper>
  );
};

export default SignupView;
